import { useCallback, useEffect, useState } from 'react';

import { AutoComplete, AutoCompleteProps } from 'primereact/autocomplete';

import { useLazySearchShipmentsQuery } from '@/app/services/lookups';
import { MIN_SEARCH_LENGTH, SELECTION_LIMIT } from '@/constants';
import { Shipment } from '@/rtk-query.gql';

import './ShipmentAutoComplete.css';

type ShipmentWithDisplay = Shipment & {
  displayName: string;
};

type ShipmentAutoCompleteProps = AutoCompleteProps & {
  className?: string;
  label?: string;
  defaultValue?: Shipment;
  appendTo?: string | null;
  defaultId?: string;
};

export const ShipmentAutoComplete = ({
  className,
  label,
  defaultValue,
  defaultId,
  appendTo = 'self',
  ...autoCompleteProps
}: ShipmentAutoCompleteProps) => {
  const [filtered, setFiltered] = useState<ShipmentWithDisplay[]>([]);
  const getDisplayValue = useCallback(
    (shipment: Shipment | null | undefined): string => {
      if (!shipment) return '';
      if (shipment.shipmentNumber) {
        return shipment.shipName
          ? `${shipment.shipmentNumber} - ${shipment.shipName}`
          : shipment.shipmentNumber;
      }
      return shipment.shipName || '';
    },
    [],
  );
  const [inputValue, setInputValue] = useState(
    defaultValue ? getDisplayValue(defaultValue) : '',
  );
  const [selected, setSelected] = useState<Shipment | undefined>(defaultValue);

  const [query] = useLazySearchShipmentsQuery();

  useEffect(() => {
    if (defaultValue) {
      if (setInputValue) {
        setInputValue(getDisplayValue(defaultValue));
      }
    } else if (defaultValue === null) {
      setInputValue('');
    } else if (defaultId) {
      query({
        search: defaultId,
        selectionLimit: SELECTION_LIMIT,
      }).then(({ data = [] }) => {
        const shipment = data.find((ship) => ship.id === defaultId);
        if (shipment) {
          const enrichedShipment = {
            ...shipment,
            displayName: getDisplayValue(shipment),
          };
          setSelected(shipment);
          setInputValue(enrichedShipment.displayName);
        }
      });
    }
  }, [defaultValue]);

  const search = useCallback(
    async (event: { query: string }) => {
      const { data = [] } = await query({
        search: event.query || '',
        selectionLimit: SELECTION_LIMIT,
      });

      setFiltered(
        data.map((shipment) => ({
          ...shipment,
          displayName: getDisplayValue(shipment),
        })),
      );
    },
    [query, getDisplayValue],
  );

  const handleSelect = (e: { value: ShipmentWithDisplay }) => {
    const shipment = e.value as Shipment;
    setSelected(shipment);
    if (setInputValue) {
      setInputValue(e.value.displayName);
    }
  };

  const handleChange = (e: { value: string | ShipmentWithDisplay }) => {
    if (setInputValue) {
      const value =
        typeof e.value === 'object' && e.value !== null
          ? e.value.displayName
          : (e.value as string);
      setInputValue(value as string);
    }
  };

  return (
    <div
      className={`[ shipment-auto-complete ] [${className ? ` ${className}` : ''} label-input-group ]`}
    >
      {label && <label>{label}</label>}
      <AutoComplete
        appendTo={appendTo}
        suggestions={filtered}
        field="displayName"
        minLength={MIN_SEARCH_LENGTH}
        selectionLimit={SELECTION_LIMIT}
        completeMethod={search}
        onSelect={handleSelect}
        onChange={handleChange}
        dropdown={true}
        {...autoCompleteProps}
        value={inputValue}
        className={className}
      />
    </div>
  );
};
